
import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { AppSettings } from '../types';
import { api } from '../services/api';
import { initAnalytics } from '../services/analyticsService';
import {
  DEFAULT_GATEWAY_RULES,
  DEFAULT_DUNNING_RULES,
  DEFAULT_TEMPLATES,
  DEFAULT_CREDENTIALS,
  DEFAULT_PORTAL_BRANDING,
  DEFAULT_CHECKOUT_CONFIG,
  DEFAULT_ANALYTICS_CONFIG,
} from './settings/defaultSettings';

interface SettingsContextType {
  settings: AppSettings;
  updateSettings: (newSettings: Partial<AppSettings>) => void;
}

const SettingsContext = createContext<SettingsContextType | undefined>(undefined);

const credentialsFromPublicSettings = (publicSettings: any) => {
  const credentials: any = {};
  const activeGateways = Array.isArray(publicSettings?.activeGateways) ? publicSettings.activeGateways : [];
  activeGateways.forEach((gateway: string) => {
    if (gateway === 'razorpay') credentials.razorpay = { keyId: '__configured__', keySecret: '__configured__' };
    if (gateway === 'stripe') credentials.stripe = { publishableKey: '__configured__' };
    if (gateway === 'cashfree') credentials.cashfree = { appId: '__configured__', secretKey: '__configured__' };
    if (gateway === 'lemonsqueezy') credentials.lemonsqueezy = { apiKey: '__configured__' };
    if (gateway === 'paddle') credentials.paddle = { vendorId: '__configured__' };
    if (gateway === 'checkoutcom') credentials.checkoutcom = { publicKey: '__configured__' };
    if (gateway === 'chargebee') credentials.chargebee = { site: '__configured__' };
  });
  return credentials;
};

export const SettingsProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [settings, setSettings] = useState<AppSettings>({
    environment: 'production',
    isGstEnabled: false,
    gstRate: 18,
    businessGstin: '',
    gatewayRules: DEFAULT_GATEWAY_RULES,
    gatewayCredentials: DEFAULT_CREDENTIALS,
    dunningRules: DEFAULT_DUNNING_RULES,
    cancellationSaverRules: [
      { id: 'csr-1', name: 'Default Saver', isEnabled: true, offerType: 'discount', discountValue: 20 },
    ],
    retentionOffers: [],
    emailTemplates: DEFAULT_TEMPLATES,
    developerSettings: {
      publicKey: 'pk_live_' + Math.random().toString(36).substring(7).toUpperCase(),
      secretKey: 'sk_live_' + Math.random().toString(36).substring(7).toUpperCase(),
      webhookSecret: 'whsec_' + Math.random().toString(36).substring(7).toUpperCase(),
      webhooks: [],
    },
    checkoutConfig: DEFAULT_CHECKOUT_CONFIG,
    portalBranding: DEFAULT_PORTAL_BRANDING,
    isAIConsultantEnabled: true,
    analyticsConfig: DEFAULT_ANALYTICS_CONFIG,
  });

  useEffect(() => {
    initAnalytics(settings.analyticsConfig);
  }, [settings.analyticsConfig]);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const isAuthenticated = api.auth.isAuthenticated();
        const remoteSettings = isAuthenticated ? await api.settings.get() : await api.settings.getPublic();
        if (remoteSettings && Object.keys(remoteSettings).length > 0) {
          setSettings((prev) => {
            const publicCredentials = isAuthenticated ? {} : credentialsFromPublicSettings(remoteSettings);
            const merged = {
              ...prev,
              ...remoteSettings,
              environment: 'production',
              gatewayCredentials: {
                ...prev.gatewayCredentials,
                ...(remoteSettings.gatewayCredentials || {}),
                ...publicCredentials,
              },
              analyticsConfig: {
                ...DEFAULT_ANALYTICS_CONFIG,
                ...(remoteSettings.analyticsConfig || {}),
              },
            };
            initAnalytics(merged.analyticsConfig);
            return merged;
          });
        }
      } catch (error) {
        console.warn('Settings loaded with local defaults', error);
      }
    };

    fetchSettings();

    const handleAuthChange = () => {
      fetchSettings();
    };

    window.addEventListener('subscale_auth_change', handleAuthChange);
    return () => window.removeEventListener('subscale_auth_change', handleAuthChange);
  }, []);

  const updateSettings = (newSettings: Partial<AppSettings>) => {
    setSettings((prev) => {
      const updated = { ...prev, ...newSettings, environment: 'production' };
      if (updated.analyticsConfig) {
        initAnalytics(updated.analyticsConfig);
      }
      if (api.auth.isAuthenticated()) {
        api.settings.update(updated).catch((err) => console.error('Failed to save settings', err));
      }
      return updated;
    });
  };

  return (
    <SettingsContext.Provider value={{ settings, updateSettings }}>
      {children}
    </SettingsContext.Provider>
  );
};

export const useSettings = () => {
  const context = useContext(SettingsContext);
  if (!context) throw new Error('useSettings must be used within a SettingsProvider');
  return context;
};

